import {
  ActivityStatus,
  ApiListRequest,
  ApiListResponse,
  AppointableCategoryDTO,
  AppointableCategoryPatchDTO,
  AppointableDTO,
  AppointableDtoStatus,
  AppointablePatchDTO,
  AppointablePostDTO,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

import { createSortParam } from "../createSortParam";
import { trimFields } from "./features/trimFiels";

export type AppointableApiType = ReturnType<typeof appointableApi>;

const appointableApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;
  const fieldsToTrim = ["name", "notes"];

  return {
    find({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: {
      readonly extraParams?: {
        readonly id?: number[];
        readonly status?: AppointableDtoStatus;
        readonly allowOnlineBookings?: boolean;
        readonly allowAppBookings?: boolean;
        readonly [k: string]: any;
      };
      readonly page?: number;
      readonly limit?: number;
      readonly sort?: {
        readonly field: string;
        readonly desc?: boolean;
      };
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get<ApiListResponse<AppointableDTO>>(
        `${clubPrefix}appointable`,
        config,
      );
    },
    findById(id: number) {
      return axiosInstance.get<AppointableDTO>(
        `${clubPrefix}appointable/${id}`,
      );
    },
    fetchCategories(
      params: { statusList?: ActivityStatus[] } & ApiListRequest,
    ) {
      return axiosInstance.put<ApiListResponse<AppointableCategoryDTO>>(
        `${clubPrefix}appointable/category/search`,
        params,
      );
    },
    create(newResource: AppointablePostDTO) {
      const trimmedData = trimFields(newResource, fieldsToTrim);

      return axiosInstance.post<AppointableDTO>(
        `${clubPrefix}appointable`,
        trimmedData,
      );
    },
    update(id: number, patchedFields: AppointablePatchDTO) {
      return axiosInstance.patch(
        `${clubPrefix}appointable/${id}`,
        trimFields(patchedFields, fieldsToTrim),
      );
    },

    createCategory(category: Omit<AppointableCategoryDTO, "id">) {
      return axiosInstance.post<AppointableCategoryDTO>(
        `${clubPrefix}appointable/category`,
        category,
      );
    },
    updateCategory({
      id,
      params,
    }: {
      id: number;
      params: AppointableCategoryPatchDTO;
    }) {
      return axiosInstance.patch(
        `${clubPrefix}appointable/category/${id}`,
        params,
      );
    },
    disableCategory(id: number) {
      return axiosInstance.patch<AppointableCategoryDTO>(
        `${clubPrefix}appointable/category/${id}/status`,
        { status: "DISABLED" },
      );
    },
    active(id: number) {
      return axiosInstance.patch<AppointableDTO>(
        `${clubPrefix}appointable/${id}/active`,
      );
    },
    disable(id: number) {
      return axiosInstance.patch<AppointableDTO>(
        `${clubPrefix}appointable/${id}/inactive`,
      );
    },
    delete(appointableId: number) {
      return axiosInstance.delete(`${clubPrefix}appointable/${appointableId}`);
    },
  };
};

export default appointableApi;
